import { Component, Input, Optional, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { EmplacementService } from 'src/app/services/emplacement-service';
import { DonneesBaseService } from 'src/app/services/donnees-base-service';
import { EvaluationFournisseur } from 'src/app/entites/evaluation-fournisseur';
import { EvaluationFournisseurComponent } from 'src/app/pages/achats/controles/evaluation-fournisseur.component';
import { EModeAffichage } from 'src/app/base/mode-affichage';
import { Emplacement } from 'src/app/entites/emplacement';
import { Fournisseur } from 'src/app/entites/fournisseur';

export interface EvaluationsGroupeesParFournisseur {
  fournisseurId: number;
  nomFournisseur: string;
  numeroFournisseur: string;
  sousTraitant: boolean;
  evaluations: EvaluationFournisseur[];
}

@Component({
  selector: 'app-evaluation-fournisseur-emplacement',
  templateUrl: './evaluation-fournisseur-emplacement.component.html',
  styleUrls: ['./evaluation-fournisseur-emplacement.component.scss'],
  standalone: false
})
export class EvaluationFournisseurEmplacementComponent implements OnInit {
  @Input() emplacementId?: number;

  groupes: EvaluationsGroupeesParFournisseur[] = [];
  filtreFournisseur = '';
  chargement = true;
  emplacement?: Emplacement;

  get groupesFiltres(): EvaluationsGroupeesParFournisseur[] {
    if (!this.filtreFournisseur?.trim()) return this.groupes;
    const term = this.filtreFournisseur.trim().toLowerCase();
    return this.groupes.filter(
      (g) =>
        (g.nomFournisseur ?? '').toLowerCase().includes(term) ||
        (g.numeroFournisseur ?? '').toLowerCase().includes(term)
    );
  }

  constructor(
    private emplacementService: EmplacementService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private router: Router,
    private sdb: DonneesBaseService,
    @Optional() private ref: DynamicDialogRef,
    @Optional() private config: DynamicDialogConfig
  ) {
    if (this.config?.data) {
      this.emplacementId = this.config.data.emplacementId ?? this.emplacementId;
      this.emplacement = this.config.data.emplacement;
    }
    if (this.ref) {
      this.router.events.subscribe(() => this.ref.close());
    }
  }

  ngOnInit(): void {
    if (this.emplacementId) {
      this.chargerEvaluations();
    }
  }

  private chargerEvaluations(): void {
    if (!this.emplacementId) return;
    this.chargement = true;
    forkJoin({
      evaluations: this.emplacementService.ObtenirEvaluationsParEmplacement(this.emplacementId),
      fournisseursBdc: this.emplacementService.ObtenirFournisseursAvecBonsCommandeParEmplacement(this.emplacementId)
    }).subscribe({
      next: ({ evaluations, fournisseursBdc }) => {
        this.groupes = this.mergerEvaluationsEtFournisseursBdc(evaluations, fournisseursBdc);
        this.chargement = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: `Une erreur est survenue lors de la récupération des données. ${err.message}`
        });
        this.chargement = false;
      }
    });
  }

  private mergerEvaluationsEtFournisseursBdc(
    evaluations: EvaluationFournisseur[],
    fournisseursBdc: Fournisseur[]
  ): EvaluationsGroupeesParFournisseur[] {
    const map = new Map<number, EvaluationsGroupeesParFournisseur>();
    for (const eval_ of evaluations) {
      const fournisseurId = eval_.fournisseur?.fournisseurId ?? eval_.fournisseurId ?? 0;
      if (!map.has(fournisseurId)) {
        map.set(fournisseurId, {
          fournisseurId,
          nomFournisseur: eval_.fournisseur?.nom ?? '',
          numeroFournisseur: eval_.fournisseur?.numero ?? '',
          sousTraitant: eval_.fournisseur?.sousTraitant === 'O',
          evaluations: []
        });
      }
      map.get(fournisseurId)!.evaluations.push(eval_);
    }
    for (const f of fournisseursBdc) {
      const fournisseurId = f.fournisseurId ?? 0;
      if (!map.has(fournisseurId)) {
        map.set(fournisseurId, {
          fournisseurId,
          nomFournisseur: f.nom ?? '',
          numeroFournisseur: f.numero ?? '',
          sousTraitant: f.sousTraitant === 'O',
          evaluations: []
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.nomFournisseur.localeCompare(b.nomFournisseur)
    );
  }

  voirFournisseur(fournisseurId: number): void {
    this.router.navigate(['/fournisseur', fournisseurId]);
  }

  afficherEvaluation(evaluation: EvaluationFournisseur): void {
    const fournisseurId = evaluation.fournisseurId;
    const sousTraitant = evaluation.fournisseur?.sousTraitant === 'O';
    this.dialogService.open(EvaluationFournisseurComponent, {
      header: 'Évaluation du fournisseur',
      width: '50%',
      data: {
        fournisseurId,
        sousTraitant,
        evaluation,
        modeAffichage: EModeAffichage.Consultation
      }
    });
  }

  evaluerFournisseur(groupe: EvaluationsGroupeesParFournisseur): void {
    const emplacement = this.emplacement ?? this.sdb.EmplacementsComplet()?.find(e => e.emplacementId === this.emplacementId);
    const evaluation: Partial<EvaluationFournisseur> = {
      emplacementId: this.emplacementId,
      emplacement,
      fournisseurId: groupe.fournisseurId,
      fournisseur: {
        fournisseurId: groupe.fournisseurId,
        nom: groupe.nomFournisseur,
        numero: groupe.numeroFournisseur
      },
      isRetained: true
    };
    this.dialogService.open(EvaluationFournisseurComponent, {
      header: `Ajouter une évaluation du ${groupe.sousTraitant ? 'sous-traitant' : 'fournisseur'}`,
      width: '50%',
      data: {
        fournisseurId: groupe.fournisseurId,
        sousTraitant: groupe.sousTraitant,
        evaluation,
        modeAffichage: EModeAffichage.Ajout
      }
    }).onClose.subscribe((eval_: EvaluationFournisseur) => {
      if (eval_) {
        const g = this.groupes.find(x => x.fournisseurId === groupe.fournisseurId);
        if (g) {
          g.evaluations.push(eval_);
          g.evaluations = [...g.evaluations];
        }
      }
    });
  }

  getQualityScore(
    evaluation: EvaluationFournisseur,
    type: 'product' | 'service' | 'pricing' | 'onsite' | 'safeWorkingPractices'
  ): string {
    let score = 0;
    let count = 0;

    if (type === 'product') {
      ['qualityExpectations', 'productAccuracy', 'quantityAccuracy', 'correctionSpeed'].forEach(
        (field) => {
          if (evaluation[field] !== 'na') {
            score += this.getScoreValue(evaluation[field]);
            count++;
          }
        }
      );
    } else if (type === 'service') {
      [
        'deliveryFlexibility',
        'deliveryDeadlines',
        'priceResponseTime',
        'serviceWarranty',
        'contactQuality'
      ].forEach((field) => {
        if (evaluation[field] !== 'na') {
          score += this.getScoreValue(evaluation[field]);
          count++;
        }
      });
    } else if (type === 'pricing') {
      ['pricingClarity', 'qualityPriceRatio', 'paymentPolicy'].forEach((field) => {
        if (evaluation[field] !== 'na') {
          score += this.getScoreValue(evaluation[field]);
          count++;
        }
      });
    } else if (type === 'onsite') {
      [
        'sstmanagement',
        'deadlineRespect',
        'workQuality',
        'documentQuality',
        'workSupervision',
        'communication',
        'qualityOfWorkers',
        'legalCompliance',
        'hamelConstructionPolicy'
      ].forEach((field) => {
        if (evaluation[field] !== 'na') {
          score += this.getScoreValue(evaluation[field]);
          count++;
        }
      });
    } else if (type === 'safeWorkingPractices') {
      [
        'safeWorkingPractices',
        'dailyAST',
        'ppeCompliance',
        'sstSupervision',
        'sstCollaborationIndice'
      ].forEach((field) => {
        if (evaluation[field] !== 'na') {
          score += this.getScoreValue(evaluation[field]);
          count++;
        }
      });
    }

    const average = count > 0 ? score / count : 0;

    if (average === 0) return 'secondary';
    if (average >= 3.5) return 'success';
    if (average >= 2.5) return 'info';
    if (average >= 1.5) return 'warning';
    return 'danger';
  }

  getQualityScoreLabel(
    evaluation: EvaluationFournisseur,
    type: 'product' | 'service' | 'pricing' | 'onsite' | 'safeWorkingPractices'
  ): string {
    const severity = this.getQualityScore(evaluation, type);
    return severity === 'success'
      ? 'Très bon'
      : severity === 'info'
        ? 'Bon'
        : severity === 'warning'
          ? 'Acceptable'
          : severity === 'secondary'
            ? 'N/A'
            : 'Mauvais';
  }

  private getScoreValue(rating: string): number {
    switch (rating) {
      case 'tres_bon':
        return 4;
      case 'bon':
        return 3;
      case 'acceptable':
        return 2;
      case 'mauvais':
        return 1;
      default:
        return 0;
    }
  }
}
